type AuthTrackingTrigger = "login" | "page_load";

type TrackResolvedAuthStateParams = {
  isAuthenticated: boolean;
  userId?: number;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (command: "event", eventName: string, params?: Record<string, unknown>) => void;
  }
}

const GTM_SCRIPT_ELEMENT_ID = "lybe-gtm-script";

let isGtmInitialized = false;
let hasTrackedInitialAuthState = false;
let previousResolvedAuthState: boolean | null = null;

const pushToDataLayer = (event: Record<string, unknown>) => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
};


const createAuthTrackingEvent = ({
  trigger,
  isAuthenticated,
  userId,
}: {
  trigger: AuthTrackingTrigger;
  isAuthenticated: boolean;
  userId?: number;
}) => ({
  event: "lybe_auth_state",
  auth_trigger: trigger,
  auth_status: isAuthenticated ? "authenticated" : "unauthenticated",
  is_authenticated: isAuthenticated,
  user_id: isAuthenticated ? (userId ?? null) : null,
});

export const initializeGoogleTagManager = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const gtmId = import.meta.env.VITE_GTM_ID;
  if (!gtmId || isGtmInitialized) return;

  if (document.getElementById(GTM_SCRIPT_ELEMENT_ID)) {
    isGtmInitialized = true;
    return;
  }

  pushToDataLayer({
    event: "gtm.js",
    "gtm.start": Date.now(),
  });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ELEMENT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  document.head.appendChild(script);
  isGtmInitialized = true;
};

export const trackResolvedAuthState = ({
  isAuthenticated,
  userId,
}: TrackResolvedAuthStateParams) => {
  let trigger: AuthTrackingTrigger | null = null;

  if (!hasTrackedInitialAuthState) {
    trigger = "page_load";
    hasTrackedInitialAuthState = true;
  } else if (previousResolvedAuthState === false && isAuthenticated) {
    trigger = "login";
  }

  previousResolvedAuthState = isAuthenticated;

  console.log(
    `Tracking auth state: ${trigger}, isAuthenticated: ${isAuthenticated}, userId: ${userId}`,
  );
  if (!trigger) return;

  const dataLayerEvent = createAuthTrackingEvent({
    trigger,
    isAuthenticated,
    userId,
  });
  console.log("Pushing to dataLayer:", dataLayerEvent);

  pushToDataLayer(dataLayerEvent);
};

